import { memo, useCallback, useRef, useEffect } from 'react'
import { RightDetailView } from '../rightDetailView/RightDetailView'
import { Matrix } from '../../components/matrix/Matrix'
import { getRelativeURLRemovingSearchParams } from '../../helpers/routeHelper'
import { MainLayout } from '../../components/layout/MainLayout'
import { ProjectLevelTopBar } from '../project/ProjectLevelTopBar'
import { OnboardingView } from '../../components/onboarding/OnboardingView'
import { useNarrowWidth } from '../../hooks/useNarrowWidth'
import { itemHelper, projectHelper } from '../../common/src/helpers'
import { uriDataCreator } from '../../common/src/helpers/URLHelper'
import { PM_API_RESOURCE_TYPE } from '../../common/src/constants'
import { linkSubject, SOURCES } from '../../reactions/linkSubject'
import { useReactToPrint } from 'react-to-print'
import { PrintableComponent } from '@/components/PrintableComponent'
import { useDispatch, useSelector } from 'react-redux'
import { setPrefersDarkMode } from '@/actions/uiActions'
import { getPrefersDarkMode } from '@/selectors/uiSelectors'
import { useHistory } from 'react-router-dom'
// Import necessary actions and hooks
import { useSearchFilterParamsEffect } from '../../hooks/filterHooks'
import { setItemsTagsFilter } from '../../common/src/actions/filtersActions'
import { FILTER_REDUCER_KEYS } from '../../common/src/reducers/filtersKeys'

// Force the application of item tags from URL
const ForceApplyItemTagsFromUrl = ({ filtersType }) => {
  const dispatch = useDispatch();
  
  // Force apply URL parameters right on component mount
  useEffect(() => {
    try {
      // Get URL parameters directly
      const params = new URLSearchParams(window.location.search);
      
      // Check for our renamed parameter
      if (params.has('item_tags')) {
        const tagParam = params.get('item_tags');
        
        // Parse tags
        const tagArray = tagParam.split(',')
          .map(tag => tag.trim())
          .filter(tag => /^[a-zA-Z0-9_-]+$/.test(tag));
        
        // Direct dispatch with a slight delay to ensure DOM is ready
        setTimeout(() => {
          dispatch(setItemsTagsFilter(tagArray, filtersType));
        }, 500);
      }
    } catch (error) {
      console.error('Error in ForceApplyItemTagsFromUrl:', error);
    }
  }, [dispatch, filtersType]);
  
  return null;
}

const ProjectMatrixView_ = ({
  project,
  items,
  selectedItemId,
  sortFn,
  loading,
  projectSelectorEnabled = true,
  readOnly = false,
  matrixPreviewMode = false,
  showOwner = true,
  filtersType,
}) => {
  const dispatch = useDispatch()
  const narrowWidth = useNarrowWidth()

  // Process URL parameters for filter values in project matrix view
  const history = useHistory()
  
  // Clear any potential item tag filtering if project_tags is present but item_tags is not
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('project_tags') && !urlParams.has('item_tags')) {
      // There's a project_tags parameter but no item_tags
      // Dispatch to clear any potential item tag filters that might have been incorrectly applied
      dispatch(setItemsTagsFilter([], FILTER_REDUCER_KEYS.PROJECT_MATRIX));
    }
  }, [dispatch]);
  
  // Use search filter params effect to update URL when filters change
  // and apply URL parameters to filters when loading the page
  useSearchFilterParamsEffect(FILTER_REDUCER_KEYS.PROJECT_MATRIX);
  
  // Check if we should clear search parameters from the URL
  useEffect(() => {
    // Check if we've navigated from search view
    const wasInSearchView = sessionStorage.getItem('isInSearchView') === 'true' || 
                          sessionStorage.getItem('clearSearchParams') === 'true';
    
    if (wasInSearchView) {
      // Clear the flags
      sessionStorage.removeItem('clearSearchParams');
      sessionStorage.removeItem('isInSearchView');
      
      // Create a clean URL without search params
      const url = new URL(window.location.href);
      const searchParams = ['q', 'item_state', 'owners', 'item_tags', 'project_ids', 'quadrant'];
      
      // Remove search-specific parameters
      searchParams.forEach(param => {
        url.searchParams.delete(param);
      });
      
      // Update the URL without triggering a page reload
      history.replace(url.pathname + (url.search ? url.search : ''));
    }
  }, [history]);

  const onClickItem = useCallback(
    item => {
      if (matrixPreviewMode) {
        return
      }
      
      // Detect if we're in a search view by checking the URL
      const isInSearchView = window.location.pathname.includes('/search/');
      
      // Get the item ID
      const serverID = itemHelper.getId(item)
      
      // Create the item URL data
      const urlData = uriDataCreator(1, PM_API_RESOURCE_TYPE.ITEM, serverID)
      
      // If we're in a search view, add a special flag to the URL data
      // to indicate that we should clear search parameters when navigating
      if (isInSearchView) {
        // Store the current path in session storage to handle the redirect
        sessionStorage.setItem('clearSearchParams', 'true');
      }
      
      // Navigate to the item
      linkSubject.next({ 
        urlData, 
        source: SOURCES.MATRIX,
      })
    },
    [matrixPreviewMode]
  )

  const mainRef = useRef()
  const selectedMode = useSelector(getPrefersDarkMode)
  const previousMode = useRef(null)

  const printMain = useReactToPrint({
    content: () => mainRef.current,
    onBeforeGetContent: async () => {
      previousMode.current = selectedMode
      dispatch(setPrefersDarkMode('light'))
    },
    onAfterPrint: () => {
      dispatch(setPrefersDarkMode(previousMode.current))
    },
  })

  const showOnboardingView = !narrowWidth && !readOnly

  const main = (
    <PrintableComponent pageName={projectHelper.getName(project)} className="print:h-screen" ref={mainRef}>
      <ForceApplyItemTagsFromUrl filtersType={filtersType} />
      <Matrix
        items={items}
        onClickItem={onClickItem}
        project={project}
        selectedItemId={selectedItemId}
        sortFn={sortFn}
        readOnly={readOnly}
        loading={loading}
        showOwner={showOwner}
        filtersType={filtersType}
      />
      {showOnboardingView && <OnboardingView />}
    </PrintableComponent>
  )
  const detail = <RightDetailView readOnly={readOnly} />
  const topBar = (
    <ProjectLevelTopBar
      getNavigationURLForProjectId={getRelativeURLRemovingSearchParams.matrixForProjectId}
      key={'ProjectMatrixViewTopBar'}
      project={project}
      projectSelectorEnabled={projectSelectorEnabled}
      readOnly={readOnly}
      items={items}
      printMain={printMain}
    />
  )
  let subViews = { main }
  if (!matrixPreviewMode) {
    subViews = {
      ...subViews,
      rightSide: detail,
      topBar,
    }
  }

  return <MainLayout {...subViews} />
}

export const ProjectMatrixView = memo(ProjectMatrixView_)
